/*
I am a simple cache manager, which provides a region-based cache based on
simple JavaScript objects.  This means that both regions and keys must be
simple strings.  Values are treated as opaque, but are stored with hard
references (JS has no alternative) so caching a value WILL prevent that
value from being garbage collected.  Thus it's important to evict values
which are no longer needed to avoid memory leaks.
*/
class CacheManager {
    constructor() {
        this.get = this.get.bind(this);
        this.has = this.has.bind(this);
        this.put = this.put.bind(this);
        this.evict = this.evict.bind(this);
        this.size = this.size.bind(this);
        this.regions = this.regions.bind(this);

        this._cache = {};
    }

    // I retrieve a value from the cache, or null if no value is currently cached
    get(region, key) {
        if ((region == null) || (key == null)) {
            return null;
        }
        const r = this._cache[region];
        if ((r != null) && (r[key] != null)) {
            return r[key];
        } else {
            return null;
        }
    }

    // I return whether a 'get' call with the same arguments will return non-null
    has(region, key) {
        if ((region == null) || (key == null)) {
            return false;
        }
        const r = this._cache[region];
        return (r != null) && (r[key] != null);
    }

    // I put a value into the cache, overwriting any previous value, and return the
    // newly cached value (for passthrough chaining).  If you want the previous
    // value (e.g., like java.util.Map), you must use 'get' to retrieve it before
    // you put the new value.
    put(region, key, value) {
        if ((region == null) || (key == null)) {
            throw new Error("CacheManager does not support the null region or key");
        }
        let r = this._cache[region];
        if ((r == null)) {
            r = (this._cache[region] = {});
        }
        return r[key] = value;
    }

    // I evict a single key, a whole region, or the all regions, depending on the
    // passed arguments and return void.  Since neither region or key can be null
    // for actual caches, omititng parameters to evict is not ambiguious.
    evict(region, key) {
        if ((region != null) && (key != null)) {
            const r = this._cache[region];
            if (r != null) {
                delete r[key];
                if (_.size(r) === 0) {
                    this.evict(region);
                }
            }
        } else if (region != null) {
            delete this._cache[region];
        } else {
            this._cache = {};
        }
        return undefined;
    }

    // I return the number of keys in the specified region, or all regions,
    // depending on the passed argument.
    size(region) {
        let r;
        if (region != null) {
            r = this._cache[region];
            if (r != null) { return _.size(r); } else { return 0; }
        } else {
            let s = 0;
            for (const n of Object.keys(this._cache)) {
                r = this._cache[n];
                s += _.size(r);
            }
            return s;
        }
    }

    // I return an object with a key per region and values of the region's size
    regions() {
        const o = {};
        for (const n of Object.keys(this._cache)) {
            const r = this._cache[n];
            o[n] = _.size(r);
        }
        return o;
    }
}

export default new CacheManager();



// WEBPACK FOOTER //
// ./app/lib/CacheManager.js